// Video Generation card — per-brand default model for video ad
// generation. Options come from GET /api/ads/video-models (derived from
// the backend's MODEL_CAPS registry, single source of truth); the pick
// persists via PATCH /api/brand/:id as videoSettings.model, preserving
// the other videoSettings keys (modelByCanvas / referenceImageCount /
// titlingEngine) the card doesn't edit.
//
// Formats the Omni models can't render (anything outside 16:9/9:16)
// automatically route to Grok 1.5 at render time — surfaced as a hint
// so the operator isn't surprised by the model stamped on those ads.

import { useEffect, useState } from 'react';
import {
  Card, CardBody, HStack, VStack, Text, Heading, Button, Badge,
  Select, Icon, useToast
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import type { Brand } from './types';

export type VideoModelOption = {
  slug: string;
  label: string;
  default: boolean;
  supportedAspectRatios: string[];
  maxReferenceImages: number;
  requiresVideoSeed: boolean;
  estCostPer8s: number | null;
};

type Props = { brand: Brand; onChanged: () => void };

export function VideoModelCard({ brand, onChanged }: Props) {
  const [models, setModels] = useState<VideoModelOption[] | null>(null);
  const [selected, setSelected] = useState<string>(brand.videoSettings?.model || '');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    apiJson<{ models: VideoModelOption[] }>('/api/ads/video-models')
      .then(r => { if (!cancelled) setModels(r.models || []); })
      .catch(() => { if (!cancelled) setModels([]); });
    return () => { cancelled = true; };
  }, []);

  // Re-sync the local pick when the brand switches (same mounted route,
  // new :id) or the persisted value changes under us (external refresh)
  // — otherwise a stale edit from the previous brand could be saved
  // onto the current one.
  useEffect(() => {
    setSelected(brand.videoSettings?.model || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand._id, brand.videoSettings?.model]);

  // '' = follow the system default (the option flagged default: true).
  const effectiveSlug = selected || models?.find(m => m.default)?.slug || '';
  const current = models?.find(m => m.slug === effectiveSlug) || null;
  const persisted = brand.videoSettings?.model || '';
  const dirty = selected !== persisted;

  const save = async () => {
    setBusy(true);
    try {
      await apiJson(`/api/brand/${brand._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoSettings: { ...(brand.videoSettings || {}), model: selected || null }
        })
      });
      toast({ title: 'Video model updated', status: 'success', duration: 2500 });
      onChanged();
    } catch (err: unknown) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outline" h="100%">
      <CardBody>
        <HStack mb={3} spacing={2}>
          <ClapperIcon />
          <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">Video Generation</Heading>
        </HStack>

        <VStack align="stretch" spacing={3}>
          <Select
            size="sm"
            value={selected}
            onChange={e => setSelected(e.target.value)}
            isDisabled={models == null}
          >
            <option value="">
              {models
                ? `System default — ${models.find(m => m.default)?.label || 'Google Omni Image-to-Video'}`
                : 'Loading models…'}
            </option>
            {(models || []).map(m => (
              <option key={m.slug} value={m.slug}>
                {m.label}{m.default ? ' (default)' : ''}
              </option>
            ))}
            {/* Persisted slug that isn't operator-selectable (env pin /
                retired model) — surface it honestly instead of a blank
                Select bound to a value with no matching option. */}
            {selected && models && !models.some(m => m.slug === selected) && (
              <option value={selected}>{selected} (legacy — not selectable)</option>
            )}
          </Select>

          {current && (
            <VStack align="stretch" spacing={1.5}>
              <HStack spacing={1.5} flexWrap="wrap">
                {current.supportedAspectRatios.map(ar => (
                  <Badge key={ar} variant="subtle" fontSize="10px" px={1.5} borderRadius="md" textTransform="none">{ar}</Badge>
                ))}
                {current.estCostPer8s != null && (
                  <Badge variant="subtle" colorScheme="green" fontSize="10px" px={1.5} borderRadius="md" textTransform="none">
                    ≈${current.estCostPer8s.toFixed(2)} / 8s
                  </Badge>
                )}
              </HStack>
              {current.requiresVideoSeed && (
                <Text fontSize="xs" color="brand.muted">
                  Transforms an existing video clip — image-seeded ads fall back to Google Omni Image-to-Video.
                </Text>
              )}
              {current.supportedAspectRatios.length <= 2 && (
                <Text fontSize="xs" color="brand.muted">
                  Formats outside {current.supportedAspectRatios.join('/')} are resized and generated with Grok Imagine 1.5 automatically.
                </Text>
              )}
            </VStack>
          )}

          <Button size="sm" variant="outline" onClick={save} isLoading={busy} isDisabled={!dirty || models == null}>
            Save
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
}

function ClapperIcon() {
  return (
    <Icon viewBox="0 0 24 24" w="16px" h="16px" color="rsViolet.500">
      <path fill="currentColor" d="M4 4h16a1 1 0 0 1 1 1v3H3V5a1 1 0 0 1 1-1zm-1 6h18v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9zm3.5-6L4 8h3l2.5-4h-3zm5 0L9 8h3l2.5-4h-3zm5 0L14 8h3l2.5-4h-3z" />
    </Icon>
  );
}
